import AppLayout from '../../components/layout/app-layout/app-layout';
import Tabs from '../../components/shared/Tabs';
import Loading from '../../components/shared/Loading';
import BtButton from '../bt-button/bt-button';
import NewsletterRail from './NewsletterRail';
import NewsletterReader from './NewsletterReader';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styles from './page-updates.module.scss';
import appState from '../../state/AppStateContainer';
import { getUnreadUpTo, hasUnreadUpdates, markUpdatesRead } from '../../state/updatesStatus';
import { useTranslation } from 'react-i18next';
import {
  getNewsletterContent,
  getNewsletters,
  Newsletter,
  NewsletterAudience,
} from '../../services/newsletterService';

const PAGE_SIZE = 10;

const PageUpdates = () => {
  const [t] = useTranslation('updates');
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const linkedAudience = params.get('tab');
  const linkedId = params.get('id');

  // null means "not known yet" — distinct from an empty list, which means the
  // tab genuinely has no newsletters.
  const [newsletters, setNewsletters] = useState<Newsletter[] | null>(null);
  const [listFailed, setListFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [content, setContent] = useState<string | null>(null);
  const [contentFailed, setContentFailed] = useState(false);

  const audiences = useMemo(() => [
    {
      key: 'purchasing' as NewsletterAudience,
      name: t('tabs.purchasing', 'För inköpare'),
      active: appState.isPurchasingActive(),
    },
    {
      key: 'sales' as NewsletterAudience,
      name: t('tabs.sales', 'För leverantörer'),
      active: appState.isSalesActive(),
    },
  ], [t]);

  const visibleAudiences = audiences.filter(x => x.active);
  const [selectedTab, setSelectedTab] = useState(() =>
    Math.max(0, visibleAudiences.findIndex(x => getUnreadUpTo()[x.key])));
  const audience = visibleAudiences[selectedTab]?.key;

  // Opening the page is what clears the menu badge, for every audience with unread letters.
  const isUnread = hasUnreadUpdates();
  useEffect(() => {
    if (isUnread) markUpdatesRead();
  }, [isUnread]);

  // A shared link carries the tab as well as the letter: without it, an ?id=
  // from the supplier tab would open on the purchasing tab and match nothing.
  useEffect(() => {
    if (!linkedAudience) return;
    const tab = visibleAudiences.findIndex(x => x.key === linkedAudience);
    if (tab >= 0) setSelectedTab(tab);
    // Only ever honoured on the first render for a given linked tab; after that
    // the user's own tab clicks own the state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedAudience]);

  // Both loads race: switch tab or letter before the previous request resolves
  // and the late answer would overwrite the current one — and stay wrong, since
  // nothing would fetch again. Every response is checked against what is still
  // wanted before it is allowed to land. The first request for a letter misses
  // the server cache and goes to Mailchimp, so the window is real.
  const wantedAudience = useRef<NewsletterAudience | undefined>();
  const wantedId = useRef<string | undefined>();

  const loadList = useCallback(async () => {
    if (!audience) return;
    wantedAudience.current = audience;
    setNewsletters(null);
    setListFailed(false);
    try {
      const loaded = await getNewsletters(audience);
      if (wantedAudience.current !== audience) return;
      setNewsletters(loaded);
    } catch (e) {
      if (wantedAudience.current !== audience) return;
      setListFailed(true);
    }
  }, [audience]);

  useEffect(() => { loadList(); }, [loadList]);

  // A ?id= in the URL makes a single letter linkable, but only wins if that
  // letter is actually in the tab that loaded.
  useEffect(() => {
    if (!newsletters?.length) return;
    const linkedExists = !!linkedId && newsletters.some(x => x.id === linkedId);
    setSelectedId(current => {
      if (current && newsletters.some(x => x.id === current)) return current;
      return linkedExists ? (linkedId as string) : newsletters[0].id;
    });
  }, [newsletters, linkedId]);

  const loadContent = useCallback(async () => {
    if (!selectedId) return;
    wantedId.current = selectedId;
    setContent(null);
    setContentFailed(false);
    try {
      const html = await getNewsletterContent(selectedId);
      if (wantedId.current !== selectedId) return;
      setContent(html);
    } catch (e) {
      if (wantedId.current !== selectedId) return;
      setContentFailed(true);
    }
  }, [selectedId]);

  useEffect(() => { loadContent(); }, [loadContent]);

  const onSelect = (id: string) => {
    setSelectedId(id);
    history.replace(`${location.pathname}?tab=${audience}&id=${id}`);
  };

  const onTabChange = (tab: number) => {
    setSelectedTab(tab);
    setQuery('');
    setVisibleCount(PAGE_SIZE);
    setSelectedId(undefined);
    setContent(null);
    setContentFailed(false);
    history.replace(`${location.pathname}?tab=${visibleAudiences[tab]?.key ?? ''}`);
  };

  const filtered = useMemo(() => {
    if (!newsletters) return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return newsletters;
    return newsletters.filter(x =>
      x.title.toLowerCase().includes(needle) || (x.summary ?? '').toLowerCase().includes(needle));
  }, [newsletters, query]);

  const selected = newsletters?.find(x => x.id === selectedId);

  const renderBody = () => {
    if (listFailed) {
      return (
        <div className={styles.pageError}>
          <p>{t('error.list', 'Nyhetsbreven kunde inte hämtas just nu.')}</p>
          <BtButton color="white" size="small" onClick={loadList}>
            {t('error.retry', 'Försök igen')}
          </BtButton>
        </div>
      );
    }

    if (newsletters === null) {
      return <div className={styles.pageLoading}><Loading type="inline" /></div>;
    }

    if (newsletters.length === 0) {
      return (
        <div className={styles.pageEmpty}>
          <p>{t('empty', 'Det finns inga nyhetsbrev för den här fliken ännu.')}</p>
        </div>
      );
    }

    return (
      <div className={styles.panes}>
        <NewsletterRail
          newsletters={filtered}
          selectedId={selectedId}
          onSelect={onSelect}
          query={query}
          onQueryChange={value => { setQuery(value); setVisibleCount(PAGE_SIZE); }}
          visibleCount={visibleCount}
          onShowMore={() => setVisibleCount(count => count + PAGE_SIZE)}
          total={newsletters.length}
        />
        <NewsletterReader
          newsletter={selected}
          html={content}
          failed={contentFailed}
          onRetry={loadContent}
        />
      </div>
    );
  };

  return (
    <AppLayout title={t('pageTitle', 'Uppdateringar i Accurator')}>
      <div className={styles.pageUpdates}>
        <Tabs
          tabs={visibleAudiences.map(x => x.name)}
          selected={selectedTab}
          onChange={onTabChange}
        />
        {renderBody()}
      </div>
    </AppLayout>
  );
};

export default PageUpdates;
